import { trackEventOncePerSession } from "./trackEvent";

import type { EventKey } from "./trackEvent";

export type TrackingSource =
  | "suggest"
  | "continuous typing"
  | "multinode"
  | "latex_import"
  | "ocr"
  | "expression"
  | "library";

/**
 * Trackable node types that fire *_created events
 */
type TrackableNodeType =
  | "variable"
  | "constant"
  | "function"
  | "operatorSymbol"
  | "group"
  | "number";

const TRACKABLE_NODE_TYPES: TrackableNodeType[] = [
  "variable",
  "constant",
  "function",
  "operatorSymbol",
  "group",
  "number",
];

const NODE_TYPE_TO_EVENT: Record<TrackableNodeType, EventKey> = {
  variable: "variable_created",
  constant: "constant_created",
  function: "function_created",
  operatorSymbol: "operator_symbol_created",
  group: "group_created",
  number: "number_created",
};

/**
 * Tracks a node creation event based on node type.
 * Fires only once per session per type.
 */
export function trackNodeTypeCreated(
  nodeType: string,
  source: TrackingSource,
  options?: { name?: string; docId?: string },
) {
  if (!TRACKABLE_NODE_TYPES.includes(nodeType as TrackableNodeType)) {
    return;
  }

  const event = NODE_TYPE_TO_EVENT[nodeType as TrackableNodeType];
  trackEventOncePerSession(event, {
    source,
    name: options?.name,
    doc_id: options?.docId,
  });
}

/**
 * Recursively walks nodes and tracks creation events for each trackable type.
 * Used for LaTeX import and OCR where we receive NodeJson trees.
 */
export function trackNodesCreated(
  nodes: { type: string; children?: { type: string; children?: unknown[] }[] }[],
  source: TrackingSource,
) {
  function walkNode(node: { type: string; children?: { type: string; children?: unknown[] }[] }) {
    trackNodeTypeCreated(node.type, source);
    node.children?.forEach((child) => walkNode(child as typeof node));
  }

  nodes.forEach(walkNode);
}
